import { type FC, type MouseEventHandler, useRef } from 'react'
import { FormattedMessage } from 'react-intl'

import { type OfferV11, type PimProductV11 } from '@redteclab/api/clients/bully'
import { type CrossSellProduct } from '@redteclab/api/clients/retail-media-xsell-service'

import { LoadingButton } from 'base-ui'

import {
  cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier,
  cartIntermediateAccessorGetSellerData,
  useMultipleProductsAddToCart,
} from '../../../../cart-intermediate'
import {
  CSW_INTERACTED_KEY,
  useSetSessionStorageValuesForOptimizelyTracking,
} from '../../../../experiments/hooks'
import {
  useOpenableControls,
  useRerenderAndExecute,
} from '../../../../generic-hooks'
import { productAccessorGetVariantCode } from '../../../model/productAccessor/productAccessorGetVariantCode'
import { ProductPageRxByPrincipleDialog } from '../../../rx-by-principle'
import { useProductPageContext } from '../../productPageContext'

type ProductCrossSellBuyTogetherAddToCartProps = {
  currentOffer: OfferV11
  currentVariant: PimProductV11
  onGtmDataLayerPush: () => void
  recommendedProduct: CrossSellProduct
}

export const ProductCrossSellBuyTogetherAddToCart: FC<
  ProductCrossSellBuyTogetherAddToCartProps
> = ({
  currentOffer,
  currentVariant,
  onGtmDataLayerPush,
  recommendedProduct,
}) => {
  const rxByPrincipleDialogControls = useOpenableControls()
  const rerenderAndExecute = useRerenderAndExecute()

  const { currentPrescriptionType, product, rxByPrincipleControls } =
    useProductPageContext()

  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const trackClick =
    useSetSessionStorageValuesForOptimizelyTracking(CSW_INTERACTED_KEY)
  const currentOfferRedpoints =
    cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier(
      currentOffer,
    )

  const sellerData = cartIntermediateAccessorGetSellerData(currentOffer)

  const products = [
    {
      id: currentOffer.offerId,
      prescriptionType: currentPrescriptionType,
      product,
      redpoints: currentOfferRedpoints,
      sellerData,
    },
    {
      id: recommendedProduct.offerId,
      prescriptionType: currentPrescriptionType,
      product: recommendedProduct,
      redpoints: Number(recommendedProduct.redpoints),
      sellerData,
    },
  ]

  const { handleAddToCart, isMutating } = useMultipleProductsAddToCart({
    currentPrescriptionType,
    products,
    rxByPrincipleSelectedReason: rxByPrincipleControls.isReasonRequired
      ? rxByPrincipleControls.selectedReason
      : undefined,
  })

  const onClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (
      rxByPrincipleControls.isReasonRequired &&
      !rxByPrincipleControls.selectedReason
    ) {
      event.preventDefault()
      rxByPrincipleDialogControls.handleOpen()

      return
    }
    void handleAddToCart()
    onGtmDataLayerPush()
    trackClick()
  }

  return (
    <>
      <LoadingButton
        className="mt-7 w-full"
        data-qa-id="buy-together-with-button"
        loading={isMutating}
        name={`buy-together-${currentVariant.upid}-${recommendedProduct.id}`}
        onClick={(event) => {
          onClick(event)
        }}
        ref={submitButtonRef}
        slots={{ root: 'a' }}
      >
        <FormattedMessage id="buyTogetherWith.button.label.addBothToCart" />
      </LoadingButton>
      {rxByPrincipleControls.isReasonRequired ? (
        <ProductPageRxByPrincipleDialog
          autoOpenOnLocationHash={false}
          // key is needed to reset form state when user clientside navigates to other product
          key={productAccessorGetVariantCode(product)}
          onClose={rxByPrincipleDialogControls.handleClose}
          onOpen={rxByPrincipleDialogControls.handleOpen}
          onReasonSelect={(reason) => {
            rxByPrincipleDialogControls.handleClose()
            rxByPrincipleControls.setSelectedReason(reason)
            rerenderAndExecute(() => {
              submitButtonRef.current?.click()
            })
          }}
          open={rxByPrincipleDialogControls.isOpen}
          productName={product.name}
          reasonSet={rxByPrincipleControls.reasonSet}
        />
      ) : null}
    </>
  )
}
